import React, {useEffect, useMemo, useRef, useState} from 'react';
import cl from "./FormLinkedItemSelect.module.css";
import {Autocomplete, CircularProgress, FormHelperText, TextField} from "@mui/material";
import ServerManager from "../API/ServerManager";
import FormControlCaption from "./FormControlCaption";
import "./helperText.css";

const ITEMS_PER_RQ_PAGE = 30;
const TYPING_TIMEOUT = 500; // [ms]

const nullNamesByGender = {m: "(не задан)", f: "(не задана)", n: "(не задано)", p: "(не заданы)"};

// label - заголовок элемента
// initSelectedId - id исходно выбранного итема. Может быть undefined (что означает, что пользователь должен будем сам что-то выбрать)
// initRefList - ассоциативный массив { id, { name } }, который должен содержать запись, соответствующую id=initSelectedId
// apiName2 - имя соответствующего серверного апи (для получения/фильтрации вариантов). нарпример, "tags", "countries"
// widthLimit - максимально допустимая ширина поля в пикс.
// controlRef - ref, предназначенный для взаимодействия с компонентом (чтение текущих данных, установки ошибки и т.п.)
// onSelect - колбэк, вызываемый каждый раз, при выборе нового значения. сигнатура: function(selectedId)
// showIds - отображать ли помимо имен еще и id опций
const FormLinkedItemSelect = ({
                                    label,
                                    initSelectedId, // undefined = пока ничего не выбрано
                                    specialOptions = [],
                                    initRefList,
                                    apiName2,
                                    widthLimit,
                                    controlRef,
                                    onSelect,
                                    showIds,
                                    renderItemExtraInfoFunc,
                                    renderInitialItemExtraInfoFunc,
                                    readOnly,
                                    itemNameFunc,
                                    size = "medium",
                                    filterExtraParams = {},
                                    dataRefs,
                                    tipText,
                                    itemFilterFunc, // item -> bool
                                    locked,
                                    lockedMessage,
                                    infoText,
                                }) => {

    const ownSpecialOptions = useMemo(() => {
        return specialOptions.map(x => ({...x, isSpecial: true}));
    }, [specialOptions]);

    const [errorMessage, setErrorMessage] = useState("")
    const [showIdInInput, setShowIdInInput] = useState(true);

    const rqIdCounter = useRef(0);
    const [status, setStatus] = useState("closed"); // "", closed, idle, mainLoad, mainError, typing
    const [lastTypingTime, setLastTypingTime] = useState(0);
    const [searchQuery, setSearchQuery] = useState("");

    const [ownDataRefs, setOwnDataRefs] = useState(dataRefs);

    const [open, setOpen] = useState(false);

    useEffect(() => {
        if (controlRef) {
            controlRef.current = {
                getSelectedId: () => mainValue.auxKey ? undefined : mainValue.id,
                validateIsSelected: validateIsSelected,
                setErrorMessage: setErrorMessage,
            };
        }
    })

    function validateIsSelected(errorMessage) {
        if (!mainValue.auxKey)
            return true;
        setErrorMessage(errorMessage);
        return false;
    }

    const correctedWidthLimit = widthLimit === undefined ? 10000 : widthLimit;

    const [mainOptions, setMainOptions] = useState([]);
    const [mainValue, setMainValue] = useState(() => {
        if (initSelectedId === undefined || locked)
            return undefinedOption;

        const foundSpecialOption = specialOptions.find(x => x.id === initSelectedId);
        if (foundSpecialOption)
            return foundSpecialOption;

        return {
            id: initSelectedId,
            name: getInitialOptionName(initSelectedId, initRefList, itemNameFunc),
            isAutoGenerated: true,
        };
    });

    function filterOptions(options) {
        if (status === "mainLoad" || status === "typing")
            return [loadingOption];

        return options;
    }

    function onFilterEdit(forceUpdate = false) {
        setStatus("typing");
        setLastTypingTime(forceUpdate ? 0 : Date.now());
        setMainOptions([]);
        rqIdCounter.current++;
    }

    async function requestMainItems() {
        rqIdCounter.current++;
        const rqId = rqIdCounter.current;
        //console.log("rqId", rqId);

        setStatus("mainLoad")
        try {
            //await new Promise(resolve => setTimeout(resolve, 1000)); // temp!
            const data = await ServerManager.getSimpleModeFilteredList(apiName2, ITEMS_PER_RQ_PAGE, searchQuery, "name", "asc", 0, filterExtraParams);
            //throw "error";
            if (rqIdCounter.current === rqId) {
                const responseList = itemFilterFunc ? data[apiName2].filter(itemFilterFunc) : data[apiName2]; // TODO? remove filter later?
                // TODO? кешировать ответ, если общее кол-во опций мало? (чтобы не переспрашивать одно и то же)

                if (itemNameFunc) {
                    for (const item of responseList)
                        item.name = itemNameFunc(item);
                }

                if (searchQuery) {
                    for (let i = ownSpecialOptions.length - 1; i >= 0; i--) {
                        if (ownSpecialOptions[i].name.includes(searchQuery))
                            responseList.unshift(ownSpecialOptions[i]);
                    }
                } else {
                    if (!mainValue.auxKey && !specialOptions.find(x => x.id === mainValue.id) && !responseList.find(x => x.id === mainValue.id)) {
                        responseList.unshift(mainValue);
                        responseList.sort((a, b) => a.name.localeCompare(b.name));
                    }
                    for (let i = ownSpecialOptions.length - 1; i >= 0; i--)
                        responseList.unshift(ownSpecialOptions[i]);
                }

                if (!data.pagination.is_last)
                    responseList.push(hintOption);

                setMainOptions(responseList);
                setOwnDataRefs(data.refs);
                setStatus("idle");
            } else {
                console.log("rq answer ignored!");
            }
        } catch (error) {
            if (rqIdCounter.current === rqId) {
                setStatus("mainError");
                setMainOptions([errorOption]);
            }
        } finally {
            if (rqIdCounter.current !== rqId) {
                //console.log("ignored! rqId", rqId);
            }
        }
    }

    // запуск/продление временной задержки во время ввода текста
    useEffect(() => {
        async function wait() {
            const leftTime = lastTypingTime + TYPING_TIMEOUT - Date.now();
            if (leftTime > 0)
                await new Promise(resolve => setTimeout(resolve, leftTime));
            if (!ignore)
                requestMainItems();
        }

        if (status !== "typing")
            return;

        let ignore = false;
        wait();
        return () => {
            ignore = true;
        }

    }, [status, lastTypingTime]);

    return (
        <div className={cl.main}>
            <FormControlCaption
                text={label}
                tipText={tipText}
            />
            <Autocomplete
                open={open}
                forcePopupIcon={readOnly ? false : undefined}
                size={size}
                /*readOnly={readOnly}*/
                disabled={Boolean(locked)}
                renderInput={params => (
                    <TextField
                        {...params}
                        error={errorMessage !== "" || locked}
                        label={locked ? lockedMessage : undefined}
                        helperText={errorMessage}
                        placeholder={mainValue.auxKey === "undefined" ? "Выбрать..." : undefined}
                        slotProps={{
                            htmlInput: {
                                ...params.inputProps,
                                readOnly: readOnly,
                                maxLength: 255,
                            },
                            input: {
                                ...params.InputProps,
                                sx: {
                                    ...params.InputProps.sx,
                                    backgroundColor: readOnly ? "#ffff0018" : "white",
                                    borderRadius: "4px",
                                },
                                endAdornment: showIds && showIdInInput && isOptionHasNormalId(mainValue) ?
                                    (<span style={{display: "flex", gap: "10px"}}>
                                        {renderItemExtraInfoFunc && !mainValue.isAutoGenerated && renderItemExtraInfoFunc(mainValue, cl.extraInfo, ownDataRefs)}
                                        {renderInitialItemExtraInfoFunc && mainValue.isAutoGenerated && renderInitialItemExtraInfoFunc(mainValue, cl.extraInfo)}
                                        {renderIdInfo(mainValue.id)}
                                        {params.InputProps.endAdornment}
                                    </span>) :
                                    (params.InputProps.endAdornment),
                            },
                            formHelperText: {
                                sx: {width: "max-content", maxWidth: "350px"}
                            }
                        }}
                    />
                )}
                options={mainOptions}
                sx={{width: `min(${correctedWidthLimit}px, 100%)`/*, pointerEvents: readOnly ? "none" : "auto"*/}}
                disableClearable
                autoHighlight /* при вводе текста сразу подсвечивается первый из доступных вариантов, что позволяет сразу клавишей Enter завершить выбор */
                blurOnSelect /* после выбора, компонент теряет фокус */
                filterOptions={filterOptions}
                noOptionsText="Варианты не найдены"
                getOptionLabel={getOptionLabel}
                clearText="Очистить"
                openText="Открыть список"
                closeText="Закрыть список"
                value={mainValue}
                getOptionKey={getOptionKey}
                isOptionEqualToValue={isOptionEqualToValue}
                getOptionDisabled={(option) => {
                    return option.auxKey !== undefined;
                }}
                onChange={(event, value) => {
                    setMainValue(value);
                    if (errorMessage)
                        setErrorMessage("");
                    if (onSelect)
                        onSelect(value.id);
                }}
                renderOption={(props2, option) => {
                    const {key, ...otherProps} = props2;

                    if (option.auxKey === "hint") {
                        return (
                            <li key={key} {...otherProps}
                                style={{
                                    display: "block",
                                    fontSize: "14px",
                                    color: "#844",
                                    fontStyle: "italic",
                                    opacity: "0.8"
                                }}>
                                <p style={{marginBottom: "-5px"}}>Показаны не все варианты!</p>
                                <p>Уточните поисковый запрос</p>
                            </li>
                        );
                    }

                    if (option.auxKey === "loading") {
                        return (
                            <li key={key} {...otherProps}
                                style={{display: "flex", gap: "10px", color: "#000", opacity: "0.6"}}>
                                <CircularProgress color="inherit" size={20}/>
                                Поиск вариантов...
                            </li>
                        );
                    }

                    if (option.auxKey === "error") {
                        return (
                            <li key={key} {...otherProps}
                                style={{
                                    display: "block",
                                    fontSize: "14px",
                                    color: "#844",
                                    fontStyle: "italic",
                                    opacity: "0.8"
                                }}>
                                <p style={{marginBottom: "-5px"}}>Сбой в системе! Не удалось запросить варианты!</p>
                                <p>Попробуйте повторить поиск</p>
                            </li>
                        );
                    }

                    return (
                        <li key={key} {...otherProps}
                            style={{display: "flex", gap: "10px"}}>
                            {option.name}
                            {renderItemExtraInfoFunc && renderItemExtraInfoFunc(option, cl.extraInfo, ownDataRefs)}
                            {showIds && isOptionHasNormalId(option) && renderIdInfo(option.id)}
                        </li>
                    );
                }}
                onInputChange={(e, v, r) => {
                    //console.log("onInputChange! v=", v, "r=", r);
                    setShowIdInInput(r !== "input");
                    if (r === "input") {
                        setSearchQuery(v);
                        onFilterEdit();
                    }
                }}
                onOpen={() => {
                    if (!readOnly) {
                        setOpen(true);
                        requestMainItems();
                    }
                }}
                onClose={(event, reason) => {
                    setOpen(false);
                    rqIdCounter.current++;
                    setMainOptions([]);
                    setSearchQuery("");
                }}
            />
            {infoText && <FormHelperText sx={{marginLeft:"14px"}} className="helperText">
                {infoText}
            </FormHelperText>}
        </div>
    );
};

// "служебные" опции
const undefinedOption = {auxKey:"undefined"}
const loadingOption = {auxKey:"loading"}
const hintOption = {auxKey:"hint"}
const errorOption = {auxKey:"error"}

// только для вывода в input (иначе компонет пытается использовать поле option.label)
function getOptionLabel(option) {
    return option.auxKey ? "" : option.name;
}

// уникальные ключи для каждой опции (по дефолту используются названия опций, что не подходит, если есть одинаковые названия)
function getOptionKey(option) {
    return option.auxKey ? option.auxKey : option.id;
}

function isOptionEqualToValue(option, value) {
    return option.id === value.id && option.auxKey === value.auxKey;
}

function isOptionHasNormalId(option) {
    return !option.auxKey && !option.isSpecial && option.id !== null && option.id !== undefined;
}

function renderIdInfo(id) {
    return <span className={cl.idInfo}>ID {id}</span>
}

// TODO? shared?
function getInitialOptionName(optionId, refList, itemNameFunc) {
    if (optionId === undefined)
        return "-undefined-";

    if (optionId === null)
        return "-null-";

    const refItem = refList?.[optionId] ?? undefined;

    if (!refItem)
        return "-refItemNotFound-";

    const name = itemNameFunc ? itemNameFunc(refItem) : refItem.name;

    if (name)
        return name;

    return "-nameNotFound-";
}

export default FormLinkedItemSelect;